/**
 * Theme controller — owns *when* tokens get re-applied (spec §1).
 *
 * Two sources can change the theme under us: Telegram's `themeChanged` event,
 * and (outside Telegram) the OS switching light/dark. Both are wired here so
 * the palette follows without a reload. `/kitchen` additionally forces a
 * scheme so both themes can be reviewed side by side.
 *
 * The Telegram SDK is loaded on the web too, so its object is there outside
 * Telegram: "inside Telegram" is `isInTelegram()`, never `getTelegram()` alone.
 */

import { getTelegram, isInTelegram } from "@/boot/telegram";
import { applyTheme, chromeColor, resolveScheme, type ColorScheme } from "@/theme/tokens";

type Listener = (scheme: ColorScheme) => void;

const listeners = new Set<Listener>();
let override: ColorScheme | null = null;
let current: ColorScheme = "light";

function readAndApply(): void {
  const tg = getTelegram();
  const params = tg?.themeParams ?? null;
  const scheme = resolveScheme(tg?.colorScheme, params, override ?? undefined);
  applyTheme(params, scheme);
  // Spec 020 D1 — Telegram's own header and background match the brand ground,
  // so no strip of Telegram colour sits above the app. Older clients lack these;
  // a browser has no Telegram header at all.
  if (isInTelegram()) {
    try {
      tg?.setHeaderColor?.(chromeColor(scheme));
      tg?.setBackgroundColor?.(chromeColor(scheme));
    } catch {
      /* an old client that rejects custom colours keeps its own */
    }
  }
  if (scheme !== current) {
    current = scheme;
    listeners.forEach((fn) => fn(scheme));
  }
}

/** Called once from the Telegram bootstrap, before first paint. */
export function initTheme(): void {
  readAndApply();

  getTelegram()?.onEvent("themeChanged", readAndApply);

  // Outside Telegram there is no themeChanged, so follow the OS instead.
  window
    .matchMedia?.("(prefers-color-scheme: dark)")
    .addEventListener?.("change", () => {
      if (!isInTelegram()) readAndApply();
    });
}

export function getColorScheme(): ColorScheme {
  return current;
}

/** `/kitchen` only: pin a scheme, or pass null to follow the client again. */
export function setSchemeOverride(scheme: ColorScheme | null): void {
  override = scheme;
  readAndApply();
}

export function subscribeToScheme(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
